import type { IShare, ISubscription, Thread } from '@wovin/core'
import type { EntityID } from '@wovin/core/applog'
import type { AppThread } from '../data/ApplogDB'
import { IpnsWatcher } from '@wovin/core/ipns'
import { dateNowIso, tryParseCID } from '@wovin/core'
import { Logger } from 'besonders-logger'
import { differenceInSeconds } from 'date-fns'
import pull from 'lodash-es/pull'
import { createEffect, createSignal, onCleanup } from 'solid-js'
import { createStore, produce } from 'solid-js/store'
import { useAgent } from '../data/agent/AgentState'
import { getApplogDB } from '../data/ApplogDB'
import { flushAllPendingBlockContent } from '../data/VMs/BlockVM'
import { runConvergenceCheck, runPendingSettles } from './co-publish-settle'
import { retrievePubToThread } from './retrievePubToThread'
import { maybeRepublishSelfRosters, syncRosterSourcesNow } from './roster-sync'
import { storageState } from './storage'
import { pushShare, updateSubFromPullData } from './store-sync'
import { setupSyncTriggers } from './sync-triggers'
import './worker-transferHandlers'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO) // eslint-disable-line unused-imports/no-unused-vars

// Safety-net-only interval now that sync-on-visibility/focus (sync-triggers.ts) covers the
// common "app came back / regained connectivity" case. This is just a long-lived backstop for
// e.g. a tab left open in the background for a long time — NOT a tight poll, so it's minutes,
// not seconds. Was 60s, which meant every subscribed share got hit every single minute for the
// entire process lifetime (the biggest data-usage offender in the sync path).
const DEFAULT_SYNC_INTERVAL = 30 * 60 // 30 minutes
let waitingForSyncLogged = false
const DEFAULT_MIN_IDLE_SECONDS = 10
// ℹ we store them in localStorage instead of applogs as it's rather device-specific
// TODO ... but getting the default from your other device would be nice :p
// Reactive store for sync state — UI components read these properties directly
const [_syncState, _setSyncState] = createStore({
	syncing: null as (null | Array<{ sub?: EntityID } | { share?: EntityID }>),
	syncInterval: localStorage.getItem('note3.sync.interval')
		? Number.parseInt(localStorage.getItem('note3.sync.interval'))
		: DEFAULT_SYNC_INTERVAL,
	minIdle: localStorage.getItem('note3.sync.minIdle')
		? Number.parseInt(localStorage.getItem('note3.sync.minIdle'))
		: DEFAULT_MIN_IDLE_SECONDS,
	errors: [] as Array<{ sub?: any; share?: any; error?: any }>,
	lastNonIdleTime: null as Date | null,
	// Shares with local changes that couldn't be pushed yet (offline / bad reception).
	// Derived from push outcomes, not a network probe — see markSharePending().
	pendingShares: [] as EntityID[],
	// Maps aren't tracked by stores — use a version signal to notify on Map changes
	_watcherVersion: 0,
})
const watchers = new Map<string, IpnsWatcher>()
const watcherConnections = new Map<string, 'connected' | 'disconnected'>()
const bumpWatcherVersion = () => _setSyncState('_watcherVersion', v => v + 1)

export const syncState = {
	get syncing() {
		return _syncState.syncing
	},
	get syncInterval() {
		return _syncState.syncInterval
	},
	get minIdle() {
		return _syncState.minIdle
	},
	get errors() {
		return _syncState.errors
	},
	get lastNonIdleTime() {
		return _syncState.lastNonIdleTime
	},
	get pendingShares() {
		return _syncState.pendingShares
	},
	get pendingCount() {
		return _syncState.pendingShares.length
	},
	get activeWatchers() {
		_syncState._watcherVersion
		return watchers.size
	},
	get watchers() {
		_syncState._watcherVersion
		return watchers
	},
	get watcherConnections() {
		_syncState._watcherVersion
		return watcherConnections
	},
}

export function setSyncInterval(newInterval: number) {
	LOG('Setting sync interval to', newInterval, 'seconds')
	_setSyncState('syncInterval', newInterval)
	localStorage.setItem('note3.sync.interval', newInterval.toString())
	stopSyncService()
	if (newInterval > 0) {
		startSyncService()
	}
}

export function setMinIdle(newMinIdle: number) {
	LOG('Setting sync minIdle to', newMinIdle, 'seconds')
	_setSyncState('minIdle', newMinIdle)
	localStorage.setItem('note3.sync.minIdle', newMinIdle.toString())
	stopSyncService()
	if (newMinIdle > 0) {
		startSyncService()
	}
}

export function setLastNonIdleTime(time: Date) {
	_setSyncState('lastNonIdleTime', time)
}

/**
 * Mark/unmark a share as having local changes that couldn't be pushed.
 * Called from the push lifecycle: a failed autopush marks pending, a
 * successful (or no-op) push clears it — no network probe needed.
 */
function markSharePending(shareId: EntityID, pending: boolean) {
	_setSyncState('pendingShares', prev => {
		const has = prev.includes(shareId)
		if (pending && !has) return [...prev, shareId]
		if (!pending && has) return prev.filter(id => id !== shareId)
		return prev
	})
}

export function startSyncService(startWithSync = false) {
	LOG(`Starting sync service`, syncState.syncInterval, startWithSync)
	// Sync-on-visibility/focus/online — the main mechanism now, previously dead code
	// (never wired up). Debounced internally, safe to call every app start.
	setupSyncTriggers()
	if (syncState.syncInterval > 0) {
		scheduleTick(startWithSync ? 5 : syncState.syncInterval) // first sync 5s after load //? good UX
	}
}

// `timer` is the periodic-schedule handle. It is mutated ONLY by scheduleTick/stopSyncService,
// which own it exclusively and may always safely replace their own pending handle — there is no
// cross-cutting mutation from doSync/syncNow anymore, so no assertion is needed to catch clashes.
let timer: NodeJS.Timeout | null = null

/** (Re)schedule the next periodic tick. Safe to call at any time — replaces any pending tick. */
function scheduleTick(intervalSeconds: number) {
	if (intervalSeconds < 1) throw ERROR(`intervalSeconds too small`, intervalSeconds)
	if (timer) clearTimeout(timer)
	timer = setTimeout(() => {
		timer = null
		if (syncState.lastNonIdleTime != null) {
			const idleTime = differenceInSeconds(new Date(), syncState.lastNonIdleTime)
			DEBUG('idle enought for sync?', { idleTime, last: syncState.lastNonIdleTime })
			if (idleTime <= (syncState.minIdle ?? DEFAULT_MIN_IDLE_SECONDS)) {
				scheduleTick(Math.max(1, (syncState.minIdle ?? DEFAULT_MIN_IDLE_SECONDS) - idleTime + 1))
				return
			}
		}
		// runSync's own finally reschedules the next tick once this one completes — don't
		// reschedule here too, or we'd double-schedule.
		void runSync()
	}, 1000 * intervalSeconds)
}

export function stopSyncService() {
	LOG(`Stopping sync service`)
	if (timer) clearTimeout(timer)
	timer = null
	stopAllWatchers()
}

/**
 * Cancel any pending periodic tick and run a sync now, returning the promise of the sync that
 * actually runs. If a sync is already in flight, joins it instead of starting a second one — the
 * caller always gets a promise that resolves only once real sync work has completed.
 * This is the entry point external callers (native bg-sync, foreground/focus/online triggers)
 * should use instead of doSync() directly.
 */
export function syncNow(args?: { share?: string; sub?: string }) {
	// Purely an optimization (skip a near-term redundant tick); runSync's finally will
	// re-schedule the next proper tick once this sync completes regardless.
	if (timer) {
		clearTimeout(timer)
		timer = null
	}
	return runSync(args)
}

export function triggerSync() {
	LOG(`[triggerSync]`)
	void syncNow()
}

/**
 * Start an IPNS watcher for a subscription with real-time updates enabled
 */
function startWatcher(sub: ISubscription) {
	if (!sub.realtime) return
	if (watchers.has(sub.id)) {
		DEBUG(`[startWatcher] Watcher already exists for ${sub.name}`)
		return
	}

	LOG(`[startWatcher] Starting watcher for ${sub.name} (${sub.id})`)

	const watcher = new IpnsWatcher(storageState.ipnsWatcherUrl, sub.id, {
		onUpdate: (record) => {
			LOG(`[IpnsWatcher] Update received for ${sub.name}:`, record.value)
			void doSync({ sub: sub.id })
		},
		onError: (error) => {
			const errorMsg = error instanceof ErrorEvent ? error.message : error instanceof Error ? error.message : String(error)
			WARN(`[IpnsWatcher] Error for ${sub.name}:`, errorMsg)
			_setSyncState('errors', produce(e => e.push({ sub, error: error instanceof Error ? error : new Error(errorMsg) })))
		},
		onConnected: () => {
			DEBUG(`[IpnsWatcher] Connected for ${sub.name}`)
			watcherConnections.set(sub.id, 'connected')
			bumpWatcherVersion()
		},
		onDisconnected: () => {
			DEBUG(`[IpnsWatcher] Disconnected for ${sub.name}`)
			watcherConnections.set(sub.id, 'disconnected')
			bumpWatcherVersion()
		},
	})

	watchers.set(sub.id, watcher)
	watcherConnections.set(sub.id, 'disconnected')
	bumpWatcherVersion()
}

/**
 * Stop and cleanup watcher for a subscription
 */
export function stopWatcher(subId: string) {
	const watcher = watchers.get(subId)
	if (!watcher) return

	LOG(`[stopWatcher] Stopping watcher for ${subId}`)
	watcher.close()

	watchers.delete(subId)
	watcherConnections.delete(subId)
	bumpWatcherVersion()
}

/**
 * Stop all active watchers (called on shutdown)
 */
function stopAllWatchers() {
	LOG(`[stopAllWatchers] Stopping ${watchers.size} watchers`)
	for (const [subId, watcher] of watchers.entries()) {
		watcher.close()
	}
	watchers.clear()
	watcherConnections.clear()
	bumpWatcherVersion()
}

/**
 * Setup watchers for subscriptions with realtime enabled
 */
export function registerWatcherSubscriptions(subscriptions: ISubscription[]) {
	// Initial setup for subscriptions with realtime enabled
	for (const sub of subscriptions) {
		if (sub.realtime && !sub.isDeleted) {
			startWatcher(sub)
		}
	}

	// NOTE: MobX reaction was removed. Caller should invoke this function when subscriptions change
	// if dynamic updates are needed in the future.
}

/**
 * SolidJS hook for managing an IPNS watcher with reactive state
 * @param ipnsId - The IPNS ID to watch (k51... or /ipns/...)
 * @param enabled - Whether the watcher should be active
 * @param onUpdate - Callback when IPNS record is updated
 * @param onError - Optional callback when a watcher error occurs (e.g. WebSocket failure).
 *   Callers can use this to disable `enabled` and stop partysocket reconnect loop.
 * @returns Connection status ('connected' | 'disconnected')
 */
export function useIpnsWatcher(
	ipnsId: () => string | undefined,
	enabled: () => boolean,
	onUpdate: (record: { value: string }) => void,
	onError?: (error: Error | Event) => void,
): () => 'connected' | 'disconnected' {
	const [status, setStatus] = createSignal<'connected' | 'disconnected'>('disconnected')

	createEffect(() => {
		const id = ipnsId()
		const isEnabled = enabled()
		const parsed = id ? tryParseCID(id) : null
		const isIPNS = parsed?.isIpns

		if (!isEnabled || !isIPNS) {
			setStatus('disconnected')
			return
		}

		LOG('[useIpnsWatcher] Starting watcher for', id)
		let watcher: IpnsWatcher | null = null

		try {
			watcher = new IpnsWatcher(storageState.ipnsWatcherUrl, id, {
				onUpdate: (record) => {
					LOG('[useIpnsWatcher] Update received:', record.value)
					onUpdate(record)
				},
				onError: (error) => {
					const errorMsg = error instanceof ErrorEvent ? error.message : error instanceof Error ? error.message : String(error)
					WARN('[useIpnsWatcher] Error:', errorMsg)
					// When the WebSocket server is down (e.g. ipfs.zt.ax doesn't support
					// /name/<ipns>/watch), give the caller a chance to stop the reconnect loop.
					onError?.(error instanceof Error ? error : error instanceof Event ? error : new Error(errorMsg))
				},
				onConnected: () => {
					DEBUG('[useIpnsWatcher] Connected')
					setStatus('connected')
				},
				onDisconnected: () => {
					DEBUG('[useIpnsWatcher] Disconnected')
					setStatus('disconnected')
				},
			})
		} catch (error) {
			WARN('[useIpnsWatcher] Failed to create watcher:', error)
		}

		onCleanup(() => {
			if (watcher) {
				LOG('[useIpnsWatcher] Cleaning up watcher')
				watcher.close()
			}
		})
	})

	return status
}

// The concurrency guard: whichever call claims `inFlight` first runs the real sync body. A second
// call for the SAME scope joins that promise (dedupe). A call for a DIFFERENT scope — e.g. a
// scoped pull for a specific sub while a full sync (which only covers autopull subs) is running —
// must not be satisfied by the other request's result, so it waits for the current one to clear
// and then actually runs, same as every other call. This preserves the old mutex+retry semantics
// (every request eventually really executes) while deduping truly-identical concurrent requests.
// `inFlight`/`inFlightArgs` are claimed synchronously — no await between the "is one running"
// check and the claim — so there's no race window for two bodies to start concurrently (the old
// `syncState.syncing` check had one: it was read before `await flushAllPendingBlockContent()`,
// leaving a gap where a second caller could pass the same check before the first had claimed
// anything).
let inFlight: Promise<void> | null = null
let inFlightArgs: { share?: string; sub?: string } | null = null

function sameScope(a: { share?: string; sub?: string }, b: { share?: string; sub?: string }) {
	return (a.share ?? null) === (b.share ?? null) && (a.sub ?? null) === (b.sub ?? null)
}

/**
 * Run a sync, or join the one already running. `doSync` is the stable public name kept for
 * existing call sites (BulletMenu, SharesSettings, SubscriptionsSettings, preview, watchers);
 * `syncNow`/`triggerSync` are the preferred entry points for "run now" callers since they also
 * clear a pending periodic tick first.
 */
export function doSync(args: { share?: string; sub?: string } = {}) {
	return runSync(args)
}

function runSync(args: { share?: string; sub?: string } = {}) {
	if (inFlight) {
		if (sameScope(args, inFlightArgs)) {
			if (!waitingForSyncLogged) {
				LOG('[doSync] Already syncing this scope, joining in-flight sync', args)
				waitingForSyncLogged = true
			} else {
				DEBUG('[doSync] Still waiting for in-flight sync', args)
			}
			return inFlight
		}
		// Different scope than what's running — its result wouldn't satisfy this request
		// (e.g. a full sync only pulls autopull subs). Wait, then actually run this one. This
		// can mean an extra, redundant pull/push right after a full sync already covered it —
		// that's cheap and self-correcting, so it's not worth the complexity of tracking whether
		// the previous run actually covered this exact scope.
		DEBUG('[doSync] Different scope in flight, queueing behind it', { requested: args, running: inFlightArgs })
		return inFlight.then(() => runSync(args), () => runSync(args))
	}
	inFlightArgs = args
	inFlight = performSync(args).finally(() => {
		inFlight = null
		inFlightArgs = null
		waitingForSyncLogged = false
		if (!args.sub && !args.share && syncState.syncInterval > 0) {
			scheduleTick(syncState.syncInterval)
		}
	})
	return inFlight
}

async function performSync({ share, sub }: { share?: string; sub?: string }) {
	if (!navigator.onLine && !(share || sub)) {
		// Gate on the *fresh* navigator.onLine (no network interface at all) rather than the
		// reachability-probe `isOnline()` signal, which is only refreshed on online/offline
		// events and goes stale on flaky mobile — leaving it stuck `false` would permanently
		// block the timer from pushing during brief good-reception windows. Attempts that fail
		// on bad reception are caught below and retried next tick.
		LOG('[doSync] Offline (no network interface) - skipping sync', { share, sub })
		return
	}

	// Flush any pending block content updates before syncing
	await flushAllPendingBlockContent()

	DEBUG('[Sync] Starting')
	const agent = useAgent()
	_setSyncState({ syncing: [], errors: [] })
	try {
		const db = getApplogDB()
		if (!db) throw ERROR(`[Sync] No DB!`, db)
		if (sub) {
			await doPull(db, [agent.subscriptionsMap.get(sub)], false)
		} else if (share) {
			const shareObj = agent.sharesMap.get(share)
			await doPush(db, [shareObj], false)
			// Co-shares also pull from their IPNS name (other co-publishers may have pushed)
			if (shareObj?.coPublish) {
				await doPullShare(db, shareObj)
			}
		} else {
			const promises = [doPull(db, agent.subscriptions, true)]
			if (!agent.hasStorageSetup) {
				DEBUG(`No storage setup - skipping push`)
			} else {
				promises.push(doPush(db, agent.shares, true))
			}
			await Promise.all(promises)
			// Auto-pull co-shares that have autopull enabled
			if (db) {
				for (const coShare of agent.shares) {
					if (!coShare.coPublish || coShare.isDeleted || !coShare.autopull) continue
					await doPullShare(db, coShare)
				}
			}
		}

		// Roster sync — discover/subscribe via roster sources and re-publish
		// self-rosters whose shares changed. Fire-and-forget and guarded so it
		// can never break the main push/pull sync (each function settles its own
		// work internally; this catch is belt-and-suspenders). Only runs on a
		// full sync (no share/sub scope), i.e. the periodic tick + `syncNow()`.
		if (!share && !sub) {
			void Promise.allSettled([syncRosterSourcesNow(), maybeRepublishSelfRosters()])
				.catch(err => WARN('[Sync] roster sync tick failed', err))

			// Co-publishing conflict resolution backstop + eventual-repair
			// guarantee. `runPendingSettles` fires any elapsed ritual settle
			// timers (a backstop for backgrounded tabs where setTimeout is
			// throttled). `runConvergenceCheck` resolves each co-share's IPNS
			// head and, if our last push is no longer live, pulls + integrates +
			// republishes — so bounded ritual retries can never lose data (worst
			// case is delayed convergence on a later tick). Only runs on a full
			// sync tick; both are fire-and-forget so they never block the next
			// push/pull cycle.
			runPendingSettles()
			if (db) {
				for (const share of agent.shares) {
					if (!share.coPublish || share.isDeleted) continue
					void runConvergenceCheck(share, db)
						.catch(err => WARN('[Sync] convergence check failed', { shareId: share.id, err }))
				}
			}
		}
	} catch (error) {
		ERROR('[Sync] failed to sync', error)
		_setSyncState('errors', produce(e => e.push({ error })))
	} finally {
		_setSyncState('syncing', null)
	}
}
/**
 * Pull data for a co-share from its IPNS name. Used for manual pull and autopull.
 * For co-shares, the IPNS name is the share's own ID (it was created as a writable IPNS name
 * and co-publishers push to it).
 */
async function doPullShare(thread: AppThread, share: IShare) {
	const agent = useAgent()
	const syncStateEntry = { share: share.id }
	_setSyncState('syncing', produce(s => s.push(syncStateEntry)))
	await agent.updateShare(share.id, { lastPullAttempt: dateNowIso() })
	try {
		const pullData = await retrievePubToThread(thread, share.id, { lastCID: share.lastPullCID, subID: share.id }, getApplogDB())
		if (pullData.didUpdate) {
			await agent.updateShare(share.id, {
				lastPull: pullData.ts.toISOString(),
				lastPullCID: pullData.cid.toString(),
			})
		}
	} catch (error) {
		ERROR('[Sync] failed to pull co-share', share, error)
		_setSyncState('errors', produce(e => e.push({ share, error })))
	} finally {
		_setSyncState(
			'syncing',
			produce(s => {
				const idx = s.indexOf(syncStateEntry)
				if (idx >= 0) s.splice(idx, 1)
			}),
		)
	}
}
async function doPull(thread: AppThread, subscriptions: ISubscription[], onlyIfAuto: boolean) {
	const agent = useAgent()
	for (const sub of subscriptions) {
		if (onlyIfAuto && !sub.autopull) continue
		DEBUG('[Sync] Pulling', sub)
		const syncStateEntry = { sub: sub.id }
		_setSyncState('syncing', produce(s => s.push(syncStateEntry)))
		await agent.updateSub(sub.id, { lastPullAttempt: dateNowIso() })
		try {
			// Pull
			const pullData = await retrievePubToThread(thread, sub.id, { lastCID: sub.lastCID, subID: sub.id }, getApplogDB())
			await updateSubFromPullData(pullData, sub.id)
		} catch (error) {
			ERROR('[Sync] failed to retrieve', sub, error)
			_setSyncState('errors', produce(e => e.push({ sub, error })))
		} finally {
			_setSyncState(
				'syncing',
				produce(s => {
					const idx = s.indexOf(syncStateEntry)
					if (idx >= 0) s.splice(idx, 1)
				}),
			)
		}
	}
}
async function doPush(thread: Thread, shares: IShare[], onlyIfAuto: boolean) {
	await Promise.all(shares.map(async (share) => {
		if (onlyIfAuto && !share.autopush) return
		DEBUG('[Sync] Pushing', share)
		const syncStateEntry = { share: share.id }
		_setSyncState('syncing', produce(s => s.push(syncStateEntry)))
		try {
			await pushShare(thread, share)
			// Pushed (or nothing to push) — no local changes waiting.
			markSharePending(share.id, false)
		} catch (error) {
			ERROR('[Sync] failed to publish', share, error)
			_setSyncState('errors', produce(e => e.push({ share, error })))
			// Local changes exist but couldn't be pushed (offline / bad reception).
			markSharePending(share.id, true)
		} finally {
			_setSyncState(
				'syncing',
				produce(s => {
					const idx = s.indexOf(syncStateEntry)
					if (idx >= 0) s.splice(idx, 1)
				}),
			)
		}
	}))
}
